export type StatName = 'speed' | 'perception' | 'energy' | 'shield' | 'structure';

export interface StatBreakdown {
  base: number;
  fromStatPoints: number;
  fromEquipment: number;
  fromBuildings: number;
  final: number;
  unit: string;
  label: string;
  /** 'add' = base + pts + equip + bldg, 'multiply' = base * pts * equip * bldg, 'dr' = multiplicative DR stacking */
  format: 'add' | 'multiply' | 'dr';
}

export const STAT_NAMES: StatName[] = ['speed', 'perception', 'energy', 'shield', 'structure'];

export const STAT_INFO: Record<StatName, { label: string; icon: string; description: string }> = {
  speed:      { label: 'SPEED',      icon: '»', description: 'Movement speed & turn rate' },
  perception: { label: 'PERCEPTION', icon: '◎', description: 'Detection, vision, accuracy & aim speed' },
  energy:     { label: 'ENERGY',     icon: '⚡', description: 'Max energy, efficiency & weapon power' },
  shield:  { label: 'SHIELD',     icon: '◆', description: 'Damage reduction' },
  structure:  { label: 'STRUCTURE',  icon: '▣', description: 'Max HP, armor & weapon power' },
};

/** XP needed to reach a given level (cumulative) */
export function xpForLevel(level: number): number {
  if (level <= 1) return 0;
  // Quadratic curve: level 2 = 25, level 5 = 250, level 10 = 1000, level 20 = 4000
  return Math.floor(25 * (level - 1) * (level - 1));
}

/** Points granted per level-up */
const POINTS_PER_LEVEL = 1;

export interface ComputedRobotStats {
  speed: number;            // tiles/sec base speed
  turnSpeed: number;        // rad/sec
  combatApproachSpeed: number;
  detectionRadius: number;
  visionRadius: number;
  accuracy: number;         // hit chance multiplier (1.0 = 100%)
  turretSpeed: number;      // turret rotation multiplier
  maxEnergy: number;
  energyDrain: number;      // per second
  weaponDamageMult: number; // multiplier on weapon damage
  damageReduction: number;  // fraction (0-1)
  maxStructure: number;     // HP
  attackSpeed: number;      // seconds between attacks
}

export class StatsSystem {
  // Persistent state
  totalXp = 0;
  level = 1;
  unspentPoints = 0;

  stats: Record<StatName, number> = {
    speed: 0,
    perception: 0,
    energy: 0,
    shield: 0,
    structure: 0,
  };

  /** Called after any mutation (addXp/allocate). Wired by GameStateManager
   *  to refresh level/stat-threshold achievements. */
  onChange: (() => void) | null = null;

  /** Add XP and check for level-ups. Returns number of new levels gained. */
  addXp(amount: number): number {
    this.totalXp += amount;
    let levelsGained = 0;
    while (this.totalXp >= xpForLevel(this.level + 1)) {
      this.level++;
      this.unspentPoints += POINTS_PER_LEVEL;
      levelsGained++;
    }
    this.onChange?.();
    return levelsGained;
  }

  /** XP progress toward next level (0-1) */
  get xpProgress(): number {
    const currentLevelXp = xpForLevel(this.level);
    const nextLevelXp = xpForLevel(this.level + 1);
    const range = nextLevelXp - currentLevelXp;
    if (range <= 0) return 1;
    return (this.totalXp - currentLevelXp) / range;
  }

  get xpToNext(): number {
    return xpForLevel(this.level + 1) - this.totalXp;
  }

  /** Allocate a point to a stat. Returns false if no points available. */
  allocate(stat: StatName): boolean {
    if (this.unspentPoints <= 0) return false;
    this.stats[stat]++;
    this.unspentPoints--;
    this.onChange?.();
    return true;
  }

  /**
   * Compute final robot stats from base values + stat points + upgrade bonuses.
   * upgradeStats comes from UpgradeSystem.getComputedStats()
   */
  computeRobotStats(upgradeStats: {
    speedMult: number;
    turnSpeedMult: number;
    detectionBonus: number;
    visionBonus: number;
    maxEnergyBonus: number;
    drainMult: number;
    damageReduction: number;
    maxStructureBonus: number;
  }, buildingBonuses?: {
    damageMult?: number;
    damageReduction?: number;
    detectionBonus?: number;
    visionBonus?: number;
  }): ComputedRobotStats {
    const s = this.stats;

    // Each stat point gives a percentage bonus
    // Speed: +6% per point
    const speedBonus = 1 + s.speed * 0.06;
    // Perception: +0.5 detection, +0.25 vision, +2% accuracy, +3% turret speed per point
    const perceptionDetection = s.perception * 0.5;
    const perceptionVision = s.perception * 0.25;
    const accuracyBonus = 1 + s.perception * 0.02;
    const turretSpeedBonus = 1 + s.perception * 0.03;
    // Energy: +10 max energy, -1.5% drain, +1.5% weapon damage per point
    const energyMaxBonus = s.energy * 10;
    const energyDrainMult = Math.max(0.3, 1 - s.energy * 0.015);
    const energyDamageMult = 1 + s.energy * 0.015;
    // Toughness: +2% damage reduction per point (diminishing)
    const shieldDR = 1 - Math.pow(0.98, s.shield);
    // Structure: +8 HP, +1.5% weapon damage per point
    const structureHP = 50 + s.structure * 8;  // base 50 HP
    const structureDamageMult = 1 + s.structure * 0.015;

    // Attack speed: slightly faster with perception
    const attackSpeedBase = 0.8;
    const attackSpeed = attackSpeedBase / (1 + s.perception * 0.02);

    // Combine with upgrade bonuses
    const baseDR = 1 - (1 - shieldDR) * (1 - upgradeStats.damageReduction);
    const finalDR = buildingBonuses?.damageReduction
      ? 1 - (1 - baseDR) * (1 - buildingBonuses.damageReduction)
      : baseDR;

    return {
      speed: 1.75 * speedBonus * upgradeStats.speedMult,
      turnSpeed: Math.PI * 1.1 * speedBonus * upgradeStats.turnSpeedMult,
      combatApproachSpeed: 1.25 * speedBonus * upgradeStats.speedMult,
      detectionRadius: 7 + perceptionDetection + upgradeStats.detectionBonus + (buildingBonuses?.detectionBonus ?? 0),
      visionRadius: 6 + perceptionVision + upgradeStats.visionBonus + (buildingBonuses?.visionBonus ?? 0),
      accuracy: accuracyBonus,
      turretSpeed: turretSpeedBonus,
      maxEnergy: 50 + energyMaxBonus + upgradeStats.maxEnergyBonus,
      energyDrain: 0.4 * energyDrainMult * upgradeStats.drainMult,
      weaponDamageMult: energyDamageMult * structureDamageMult * (buildingBonuses?.damageMult ?? 1),
      damageReduction: Math.min(1, Math.max(0, finalDR)),
      maxStructure: structureHP + upgradeStats.maxStructureBonus,
      attackSpeed,
    };
  }

  // ─── Stat breakdown for UI panels ─────────────────────

  /**
   * Compute final robot stats AND per-source breakdown for the stats panel.
   */
  computeRobotStatsWithBreakdown(upgradeStats: {
    speedMult: number;
    turnSpeedMult: number;
    detectionBonus: number;
    visionBonus: number;
    maxEnergyBonus: number;
    drainMult: number;
    damageReduction: number;
    maxStructureBonus: number;
  }, buildingBonuses?: {
    damageMult?: number;
    damageReduction?: number;
    detectionBonus?: number;
    visionBonus?: number;
  }): { stats: ComputedRobotStats; breakdown: Record<string, StatBreakdown> } {
    const s = this.stats;
    const computed = this.computeRobotStats(upgradeStats, buildingBonuses);

    const speedPointMult = 1 + s.speed * 0.06;
    const perceptionDetection = s.perception * 0.5;
    const perceptionVision = s.perception * 0.25;
    const energyMaxBonus = s.energy * 10;
    const shieldDR = 1 - Math.pow(0.98, s.shield);
    const structureHP = s.structure * 8;

    const breakdown: Record<string, StatBreakdown> = {
      speed: {
        base: 1.75, fromStatPoints: speedPointMult, fromEquipment: upgradeStats.speedMult,
        fromBuildings: 0, final: computed.speed, unit: 't/s', label: 'Movement Speed', format: 'multiply',
      },
      turnSpeed: {
        base: +(Math.PI * 1.1).toFixed(2), fromStatPoints: speedPointMult, fromEquipment: upgradeStats.turnSpeedMult,
        fromBuildings: 0, final: computed.turnSpeed, unit: 'rad/s', label: 'Turn Speed', format: 'multiply',
      },
      detectionRadius: {
        base: 7, fromStatPoints: perceptionDetection, fromEquipment: upgradeStats.detectionBonus,
        fromBuildings: buildingBonuses?.detectionBonus ?? 0, final: computed.detectionRadius, unit: 'tiles', label: 'Detection', format: 'add',
      },
      visionRadius: {
        base: 6, fromStatPoints: perceptionVision, fromEquipment: upgradeStats.visionBonus,
        fromBuildings: buildingBonuses?.visionBonus ?? 0, final: computed.visionRadius, unit: 'tiles', label: 'Vision', format: 'add',
      },
      accuracy: {
        base: 1, fromStatPoints: s.perception * 0.02, fromEquipment: 0,
        fromBuildings: 0, final: computed.accuracy, unit: 'x', label: 'Accuracy', format: 'add',
      },
      maxEnergy: {
        base: 50, fromStatPoints: energyMaxBonus, fromEquipment: upgradeStats.maxEnergyBonus,
        fromBuildings: 0, final: computed.maxEnergy, unit: '', label: 'Max Energy', format: 'add',
      },
      energyDrain: {
        base: 0.4, fromStatPoints: Math.max(0.3, 1 - s.energy * 0.015), fromEquipment: upgradeStats.drainMult,
        fromBuildings: 0, final: computed.energyDrain, unit: '/s', label: 'Energy Drain', format: 'multiply',
      },
      weaponDamageMult: {
        base: 1, fromStatPoints: (1 + s.energy * 0.015) * (1 + s.structure * 0.015), fromEquipment: 0,
        fromBuildings: buildingBonuses?.damageMult ?? 1, final: computed.weaponDamageMult, unit: 'x', label: 'Weapon Dmg', format: 'multiply',
      },
      damageReduction: {
        base: 0, fromStatPoints: shieldDR, fromEquipment: upgradeStats.damageReduction,
        fromBuildings: buildingBonuses?.damageReduction ?? 0, final: computed.damageReduction, unit: '%', label: 'Dmg Reduction', format: 'dr',
      },
      maxStructure: {
        base: 50, fromStatPoints: structureHP, fromEquipment: upgradeStats.maxStructureBonus,
        fromBuildings: 0, final: computed.maxStructure, unit: 'HP', label: 'Max HP', format: 'add',
      },
    };

    return { stats: computed, breakdown };
  }

  // ─── Serialization ─────────────────────────────────────

  serialize(): { totalXp: number; level: number; unspentPoints: number; stats: Record<StatName, number> } {
    return {
      totalXp: this.totalXp,
      level: this.level,
      unspentPoints: this.unspentPoints,
      stats: { ...this.stats },
    };
  }

  deserialize(data: { totalXp: number; level: number; unspentPoints: number; stats: Record<StatName, number> }) {
    this.totalXp = data.totalXp;
    this.level = data.level;
    this.unspentPoints = data.unspentPoints;
    this.stats = { ...data.stats };
  }
}
